import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-forest hover:underline mb-8">
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>
        
        <h1 className="font-heading font-bold text-3xl mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Last updated: February 2025</p>
        
        <div className="prose prose-stone max-w-none space-y-6">
          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">1. Introduction</h2>
            <p className="text-muted-foreground">
              Rise for Bakeries CRM ("Rise," "we," "us," or "our") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when 
              you use our lead generation and customer relationship management application.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">2. Information We Collect</h2>
            <p className="text-muted-foreground mb-3">We collect information that you provide directly to us:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Account Information:</strong> When you sign in with Google, we receive your name, email address, and profile picture.</li>
              <li><strong>Company Profile:</strong> Business information you provide for email outreach (company name, products, contact details).</li>
              <li><strong>Lead Data:</strong> Information about potential business leads discovered through our platform.</li>
              <li><strong>Email Content:</strong> Drafts and sent emails created through our platform.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">3. Google API Services</h2>
            <p className="text-muted-foreground mb-3">
              Rise uses Google API Services to provide email and calendar functionality. Our use of information 
              received from Google APIs adheres to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="text-forest hover:underline">
                Google API Services User Data Policy
              </a>, including the Limited Use requirements.
            </p>
            <p className="text-muted-foreground mb-3">We access the following Google services:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Gmail:</strong> To send outreach emails on your behalf. We only send emails that you explicitly approve.</li>
              <li><strong>Google Calendar:</strong> To schedule meetings with leads.</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              We do not store the content of your emails beyond what is necessary for the application's functionality. 
              We do not share your Google data with third parties except as required to provide our services.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">4. How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>To provide and maintain our service</li>
              <li>To personalize your experience and outreach emails</li>
              <li>To discover and analyze potential business leads</li>
              <li>To send emails and schedule calendar events on your behalf</li>
              <li>To improve our application and develop new features</li>
              <li>To communicate with you about your account</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">5. Data Storage and Security</h2>
            <p className="text-muted-foreground">
              Your data is stored securely using industry-standard encryption and security practices. 
              We use secure cloud infrastructure and implement appropriate technical and organizational 
              measures to protect your personal information against unauthorized access, alteration, 
              disclosure, or destruction.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">6. Data Sharing</h2>
            <p className="text-muted-foreground mb-3">We do not sell your personal information. We may share your information only in the following circumstances:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Service Providers:</strong> With third-party services that help us operate our application (cloud hosting, AI services).</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">7. Your Rights</h2>
            <p className="text-muted-foreground mb-3">You have the right to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Disconnect Google services at any time through Settings</li>
              <li>Export your lead data</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">8. Data Retention</h2>
            <p className="text-muted-foreground">
              We retain your data for as long as your account is active or as needed to provide you services. 
              You can request deletion of your account and associated data at any time by contacting us.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">9. Changes to This Policy</h2>
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">10. Contact Us</h2>
            <p className="text-muted-foreground">
              If you have any questions about this Privacy Policy, please contact us at:{' '}
              <a href="mailto:hello@maddonutshop.com" className="text-forest hover:underline">
                hello@maddonutshop.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
